import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { DocumentPlusIcon, ClipboardDocumentListIcon, ArrowTopRightOnSquareIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { FormTemplateScope, FormType, type FormTemplateSummary } from '@/types'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { fetchFormTemplates } from '../store/slices/formTemplateSlice'
import { createFormInstanceThunk, fetchProjectInstances, deleteFormInstanceThunk } from '../store/slices/formInstanceSlice'
import { useToast } from '../components/ToastContainer'
import FieldAssignmentModal from '../components/forms/FieldAssignmentModal'
import { usePermissions, Permission } from '../hooks/usePermissions'

const parseDate = (value?: string | null): Date | null => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const isTemplateActive = (template: FormTemplateSummary, referenceDate = new Date()): boolean => {
  if (template.isAlwaysActive) {
    return true
  }
  const start = parseDate(template.activationStartDate)
  const end = parseDate(template.activationEndDate)

  if (start && referenceDate < start) {
    return false
  }

  if (end && referenceDate > end) {
    return false
  }

  return true
}

const formatActivationWindow = (template: FormTemplateSummary): string => {
  if (template.isAlwaysActive) {
    return '啟用：長駐'
  }
  const start = parseDate(template.activationStartDate)
  const end = parseDate(template.activationEndDate)
  const format = (date: Date) => date.toLocaleDateString()

  if (start && end) {
    return `啟用：${format(start)} ~ ${format(end)}`
  }

  if (start) {
    return `啟用：自 ${format(start)} 起`
  }

  if (end) {
    return `啟用：至 ${format(end)} 止`
  }

  return '啟用：未設定（一次性）'
}

const ProjectFormsPage = () => {
  const { projectKey } = useParams<{ projectKey: string }>()
  const dispatch = useAppDispatch()
  const { currentProject } = useAppSelector(state => state.project)
  const { templates, loading: templatesLoading, error: templatesError } = useAppSelector(state => state.formTemplates)
  const { instances, loading: instancesLoading } = useAppSelector(state => state.formInstances)
  const { user } = useAppSelector(state => state.auth)
  const { showSuccess, showError } = useToast()
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [instanceTitle, setInstanceTitle] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [creating, setCreating] = useState(false)
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false)
  const [assignmentTemplate, setAssignmentTemplate] = useState<FormTemplateSummary | null>(null)

  const projectName = useMemo(() => currentProject?.name ?? projectKey ?? '專案', [currentProject?.name, projectKey])
  const { hasPermission, isAdmin, isSuperAdmin } = usePermissions(currentProject?.id)

  useEffect(() => {
    dispatch(fetchFormTemplates(undefined))
  }, [dispatch])

  useEffect(() => {
    if (currentProject?.id) {
      dispatch(fetchProjectInstances(currentProject.id))
    }
  }, [dispatch, currentProject?.id])

  const categorizedTemplates = useMemo(() => {
    const projectId = currentProject?.id ?? null
    const now = new Date()

    const buckets = {
      active: [] as FormTemplateSummary[],
      upcoming: [] as FormTemplateSummary[],
      expired: [] as FormTemplateSummary[],
    }

    templates.forEach(template => {
      const matchScope =
        template.scopeType === FormTemplateScope.PROJECT
          ? template.projectId === projectId
          : template.scopeType === FormTemplateScope.ORGANIZATION

      if (!matchScope) {
        return
      }

      if (isTemplateActive(template, now)) {
        buckets.active.push(template)
        return
      }

      const start = parseDate(template.activationStartDate)
      if (start && start > now) {
        buckets.upcoming.push(template)
        return
      }

      buckets.expired.push(template)
    })

    return buckets
  }, [templates, currentProject?.id])

  const relevantTemplates = useMemo(() => categorizedTemplates.active, [categorizedTemplates.active])
  const upcomingTemplates = categorizedTemplates.upcoming
  const expiredTemplates = categorizedTemplates.expired

  const projectInstances = useMemo(() => instances, [instances])

  const selectedTemplate = useMemo(
    () => relevantTemplates.find(template => template.id === selectedTemplateId) ?? null,
    [relevantTemplates, selectedTemplateId],
  )

  const selectTemplate = (template: FormTemplateSummary, options?: { forceTitle?: boolean }) => {
    setSelectedTemplateId(template.id)
    setInstanceTitle(prev => {
      if (options?.forceTitle || !prev) {
        return `${template.name} 任務`
      }
      return prev
    })
  }

  const openCreateModal = (template?: FormTemplateSummary) => {
    setCreateModalOpen(true)
    if (template) {
      selectTemplate(template, { forceTitle: true })
    } else if (relevantTemplates.length > 0) {
      selectTemplate(relevantTemplates[0], { forceTitle: true })
    } else {
      setSelectedTemplateId('')
      setInstanceTitle('')
    }
    setDueDate('')
  }

  const closeCreateModal = () => {
    setCreateModalOpen(false)
    setCreating(false)
  }

  const openAssignmentModal = (template: FormTemplateSummary) => {
    setAssignmentTemplate(template)
    setAssignmentModalOpen(true)
  }

  const closeAssignmentModal = () => {
    setAssignmentModalOpen(false)
    setAssignmentTemplate(null)
  }

  const handleCreateInstance = async () => {
    if (!selectedTemplate) {
      showError('請先選擇要派送的模板')
      return
    }

    const projectId = currentProject?.id

    try {
      setCreating(true)
      await dispatch(
        createFormInstanceThunk({
          templateId: selectedTemplate.id,
          title: instanceTitle.trim() || `${selectedTemplate.name} 任務`,
          projectId: projectId ?? undefined,
          dueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
        }),
      ).unwrap()
      if (projectId) {
        dispatch(fetchProjectInstances(projectId))
      }
      showSuccess('表單任務已建立')
      closeCreateModal()
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '請稍後再試'
      showError('建立表單任務失敗', message)
    } finally {
      setCreating(false)
    }
  }

  const canDeleteInstance = isAdmin || isSuperAdmin || hasPermission(Permission.MANAGE_TEMPLATES)

  const handleDeleteInstance = async (instanceId: string) => {
    if (!window.confirm('確認刪除此表單任務？此動作無法復原')) return
    try {
      await dispatch(deleteFormInstanceThunk(instanceId)).unwrap()
      showSuccess('已刪除表單任務')
      if (currentProject?.id) {
        dispatch(fetchProjectInstances(currentProject.id))
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '請稍後再試'
      showError('刪除表單任務失敗', message)
    }
  }

  return (
    <div className="p-6 space-y-6">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{projectName} · 表單中心</h2>
          <p className="text-sm text-gray-500">集中管理此專案可用的表單模板與派送中的表單任務。</p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/forms/templates"
            className="inline-flex items-center gap-2 rounded-md border border-primary-600 px-3 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50"
          >
            <ClipboardDocumentListIcon className="h-4 w-4" />
            前往全域模板庫
          </Link>
          <button
            type="button"
            onClick={() => openCreateModal()}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-70"
            disabled={relevantTemplates.length === 0}
          >
            <DocumentPlusIcon className="h-4 w-4" />
            建立新表單任務
          </button>
        </div>
      </header>

      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="text-base font-semibold text-gray-900">表單任務追蹤</h3>
        <p className="mt-1 text-sm text-gray-500">
          建立表單任務後會顯示在此，協助你追蹤負責人、狀態與截止時間。
        </p>
        <div className="mt-4">
          {instancesLoading ? (
            <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
              讀取任務中…
            </div>
          ) : projectInstances.length > 0 ? (
            <ul className="space-y-3">
              {projectInstances.map(instance => {
                const metadata = (instance.metadata ?? {}) as Record<string, unknown>
                const isAutoGenerated = Boolean(metadata.autoGenerated)
                const scheduleDate = typeof metadata.scheduleDate === 'string' ? metadata.scheduleDate : null
                const scheduledFieldCount = Array.isArray(metadata.scheduleFieldIds)
                  ? (metadata.scheduleFieldIds as unknown[]).length
                  : null

                return (
                  <li key={instance.id} className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-gray-900">{instance.title}</h4>
                          {isAutoGenerated && (
                            <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-[11px] font-medium text-yellow-700">
                              自動產生
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          狀態：{instance.status}
                          {instance.dueDate && ` · 截止 ${new Date(instance.dueDate).toLocaleDateString()}`}
                          {scheduleDate && ` · 適用日期 ${scheduleDate}`}
                        </p>
                        {scheduledFieldCount && scheduledFieldCount > 0 && (
                          <p className="text-xs text-gray-500">本次需檢查 {scheduledFieldCount} 項</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/project/${projectKey}/forms/instances/${instance.id}`}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                        >
                          查看表單
                          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                        </Link>
                        {canDeleteInstance && (
                          <button
                            type="button"
                            onClick={() => handleDeleteInstance(instance.id)}
                            className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                          >
                            刪除
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          ) : (
            <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
              尚未派送表單任務。一旦從模板建立並指派成員後，任務會出現在這裡。
            </div>
          )}
        </div>
      </section>

      {upcomingTemplates.length > 0 && (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-5 shadow-sm">
          <h3 className="text-base font-semibold text-amber-800">即將啟用的模板</h3>
          <p className="mt-1 text-sm text-amber-700">啟用日期尚未到達，屆時會自動出現在可派送清單。</p>
          <ul className="mt-3 space-y-2">
            {upcomingTemplates.map(template => (
              <li key={template.id} className="rounded border border-amber-200 bg-white px-3 py-2 text-sm text-amber-800">
                <div className="font-medium text-amber-900">{template.name}</div>
                <div className="text-xs text-amber-700">{formatActivationWindow(template)}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {expiredTemplates.length > 0 && (
        <section className="rounded-lg border border-gray-300 bg-gray-100 p-5 shadow-sm">
          <h3 className="text-base font-semibold text-gray-800">已結束的模板</h3>
          <p className="mt-1 text-sm text-gray-600">啟用期間已結束，可調整設定後重新啟用派送。</p>
          <ul className="mt-3 space-y-2">
            {expiredTemplates.map(template => (
              <li key={template.id} className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700">
                <div className="font-medium text-gray-800">{template.name}</div>
                <div className="text-xs text-gray-500">{formatActivationWindow(template)}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {assignmentTemplate && currentProject?.id && (
        <FieldAssignmentModal
          open={assignmentModalOpen}
          projectId={currentProject.id}
          templateId={assignmentTemplate.id}
          templateName={assignmentTemplate.name}
          members={currentProject.members ?? []}
          onClose={closeAssignmentModal}
        />
      )}

      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="text-base font-semibold text-gray-900">整合指引</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-600">
          <li>左側導覽現已提供「表單」分頁，可快速回到此中心頁面。</li>
          <li>點擊「建立新表單任務」可同時瀏覽模板概覽、調整欄位負責人並派送任務。</li>
          <li>若需新增或編輯模板，可透過上方連結前往全域模板庫。</li>
        </ul>
      </section>

      {createModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-4xl rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">建立表單任務</h3>
            <p className="mt-1 text-sm text-gray-500">
              選擇要派送的模板，檢視啟用時段與標籤後，再設定任務名稱與截止日期。
            </p>

            <div className="mt-5 flex flex-col gap-4 lg:flex-row">
              <div className="lg:w-5/12">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500">表單模板概覽</span>
                  <Link
                    to="/forms/templates"
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
                    onClick={event => event.stopPropagation()}
                  >
                    管理模板
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                  </Link>
                </div>
                <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  {templatesLoading ? (
                    <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white p-3 text-xs text-gray-500">
                      <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
                      讀取模板中…
                    </div>
                  ) : templatesError ? (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-600">
                      {templatesError}
                    </div>
                  ) : relevantTemplates.length > 0 ? (
                    <ul className="max-h-80 space-y-2 overflow-y-auto pr-1">
                      {relevantTemplates.map(template => {
                        const isSelected = template.id === selectedTemplateId
                        return (
                          <li key={template.id}>
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => selectTemplate(template)}
                              onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault()
                                  selectTemplate(template)
                                }
                              }}
                              className={`group rounded-md border px-3 py-3 text-left text-sm outline-none transition ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-50 shadow-sm ring-2 ring-primary-100'
                                  : 'border-gray-200 bg-white hover:border-primary-300 hover:bg-primary-50/40 focus:border-primary-400 focus:ring-2 focus:ring-primary-100'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <h4 className="text-sm font-semibold text-gray-900">{template.name}</h4>
                                    <span className="rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-600">
                                      {template.scopeType === FormTemplateScope.PROJECT ? '專案模板' : '全域模板'}
                                    </span>
                                    <span
                                      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                        template.formType === FormType.PERIODIC
                                          ? 'bg-amber-50 text-amber-600'
                                          : 'bg-gray-200 text-gray-600'
                                      }`}
                                    >
                                      {template.formType === FormType.PERIODIC ? '定期檢查' : '一次性'}
                                    </span>
                                  </div>
                                  {template.latestVersion && (
                                    <p className="mt-1 text-xs text-gray-500">
                                      最新版本 v{template.latestVersion.versionNumber} · {template.latestVersion.status}
                                    </p>
                                  )}
                                  {template.tags.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                      {template.tags.slice(0, 4).map(tag => (
                                        <span key={tag} className="rounded bg-white px-2 py-0.5 text-[11px] text-gray-600">
                                          #{tag}
                                        </span>
                                      ))}
                                      {template.tags.length > 4 && (
                                        <span className="rounded bg-white px-2 py-0.5 text-[11px] text-gray-500">
                                          +{template.tags.length - 4}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  <div className="mt-2 text-xs text-gray-500">{formatActivationWindow(template)}</div>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  {template.formType === FormType.PERIODIC && currentProject?.id && (
                                    <button
                                      type="button"
                                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-100"
                                      onClick={event => {
                                        event.stopPropagation()
                                        selectTemplate(template)
                                        openAssignmentModal(template)
                                      }}
                                    >
                                      <UserGroupIcon className="h-3.5 w-3.5" />
                                      欄位負責人
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  ) : (
                    <div className="rounded-md border border-dashed border-gray-300 bg-white p-3 text-xs text-gray-500">
                      目前沒有符合條件的模板，可調整啟用時段或前往模板庫建立新模板。
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 space-y-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  {selectedTemplate ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">{selectedTemplate.name}</span>
                        <span className="rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-600">
                          {selectedTemplate.scopeType === FormTemplateScope.PROJECT ? '專案模板' : '全域模板'}
                        </span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            selectedTemplate.formType === FormType.PERIODIC
                              ? 'bg-amber-50 text-amber-600'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {selectedTemplate.formType === FormType.PERIODIC ? '定期檢查' : '一次性'}
                        </span>
                      </div>
                      {selectedTemplate.latestVersion && (
                        <p className="text-xs text-gray-500">
                          最新版本 v{selectedTemplate.latestVersion.versionNumber} · {selectedTemplate.latestVersion.status}
                        </p>
                      )}
                      {selectedTemplate.description && (
                        <p className="text-xs text-gray-600">{selectedTemplate.description}</p>
                      )}
                      {selectedTemplate.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {selectedTemplate.tags.map(tag => (
                            <span key={tag} className="rounded bg-white px-2 py-0.5 text-[11px] text-gray-600">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="text-xs text-gray-500">{formatActivationWindow(selectedTemplate)}</div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">請先在左側選擇要派送的模板。</div>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">任務名稱</label>
                  <input
                    type="text"
                    value={instanceTitle}
                    onChange={e => setInstanceTitle(e.target.value)}
                    placeholder="例如：10 月份巡檢任務"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">截止日期（選填）</label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={e => setDueDate(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCreateModal}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                disabled={creating}
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleCreateInstance}
                disabled={creating || relevantTemplates.length === 0 || !selectedTemplate}
                className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {creating ? '建立中…' : '建立任務'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectFormsPage
